import { delay, put, select, takeLatest } from 'typed-redux-saga/macro';

import {
  KEYWORD_MATCHES_READY,
  KEYWORD_SEARCH,
  SET_KEYWORD_SEARCH_OPTIONS,
  SET_PAGE_TEXT,
  SET_PAGE_TEXT_BATCH,
} from '../actions';
import { findKeywordMatches, stripItemSeams } from '../helpers';
import { selectSession } from '../selectors';
import { KEYWORD_SEARCH_DEBOUNCE_MS } from '../store';
import { KeywordMatch } from '../models';

function* runKeywordSearch(phrase: string) {
  const session = yield* select(selectSession);
  if (!session) {
    return;
  }

  // Normalize once so KEYWORD_MATCHES_READY stamps the trimmed phrase
  // into state — without this, a whitespace-only input (or one with
  // leading/trailing spaces) would round-trip back as the persisted
  // value, breaking strict-equality consumers that decide whether a
  // search is active.
  const normalizedPhrase = phrase.trim();
  if (!normalizedPhrase) {
    yield* put(
      KEYWORD_MATCHES_READY({ phrase: normalizedPhrase, matches: [] })
    );
    return;
  }

  const pageTexts: Record<number, string> = session.pageTexts ?? {};
  const pageTextItems = session.pageTextItems ?? {};
  const matches: KeywordMatch[] = [];
  const { matchCase, wholeWord } = session.keywordSearch;

  // Iterate the pages that have actually delivered their text rather
  // than 1..session.totalPages. `session.totalPages` is only a seed
  // (pdfjs's real count isn't written back to session state), so looping
  // by it would cap the search short on longer PDFs. `pageTexts` is the
  // ground truth
  // of "pages we've extracted text for"; sorting the numeric keys
  // keeps results in document order so prev/next nav still walks
  // matches top-to-bottom.
  const pagesWithText = Object.keys(pageTexts)
    .map((p) => Number(p))
    .filter((p) => Number.isFinite(p) && p > 0)
    .sort((a, b) => a - b);
  for (const page of pagesWithText) {
    const text = pageTexts[page] ?? '';
    const items = pageTextItems[page] ?? [];
    const spans = findKeywordMatches(text, normalizedPhrase, {
      matchCase,
      wholeWord,
    });

    // Pre-compute each item's char range in the concatenated page text.
    // `buildPageText` puts exactly ONE separator char (a space or a zero-width
    // seam) between items, so each item still occupies `length` chars followed
    // by 1 separator — this offset math is unchanged by the seam work.
    const itemRanges: Array<{ start: number; end: number; idx: number }> = [];
    {
      let offset = 0;
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (!it) {
          continue;
        }
        itemRanges.push({ start: offset, end: offset + it.length, idx: i });
        offset += it.length + 1;
      }
    }

    // `findKeywordMatches` returns ascending offsets and `itemRanges` is
    // built in document order, so we can walk both with a single pointer
    // instead of scanning `itemRanges` from the start for each match.
    // Reduces O(matches × items) → O(matches + items).
    let itemPtr = 0;
    for (const { index: startIdx, length: matchLength } of spans) {
      const endIdx = startIdx + matchLength;
      // Strip seams: the matched run can cross item seams (a word split across
      // items / a per-glyph page), which are internal markers, not text.
      const matchedText = stripItemSeams(text.slice(startIdx, endIdx));
      // Advance the pointer past any items that end before the current
      // match starts. After this loop, `itemRanges[itemPtr]` is the
      // first range that could contain `startIdx`.
      while (
        itemPtr < itemRanges.length &&
        itemRanges[itemPtr]!.end <= startIdx
      ) {
        itemPtr++;
      }
      // Union the rects of EVERY item the match covers, clipped to the matched
      // chars at each end — so a match spanning several items (a per-glyph
      // word, or one split across items) highlights the whole phrase, not just
      // its first item. A single-item match reduces to the tight in-item rect.
      const PAD = 2; // headroom for char-width approximation, at user-space scale
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (
        let j = itemPtr;
        j < itemRanges.length && itemRanges[j]!.start < endIdx;
        j += 1
      ) {
        const rng = itemRanges[j]!;
        const item = items[rng.idx];
        if (!item) {
          continue;
        }
        const from = Math.max(startIdx, rng.start) - rng.start;
        const to = Math.min(endIdx, rng.end) - rng.start;
        if (to <= from) {
          continue; // a separator-only overlap contributes no glyph
        }
        const charWidth = item.length > 0 ? item.width / item.length : 0;
        minX = Math.min(minX, item.x + from * charWidth);
        maxX = Math.max(maxX, item.x + to * charWidth);
        minY = Math.min(minY, item.y);
        maxY = Math.max(maxY, item.y + item.height);
      }
      const rect =
        maxX > minX
          ? {
              x: minX - PAD,
              y: minY - PAD,
              width: Math.max(maxX - minX, 4) + PAD * 2,
              height: maxY - minY + PAD * 2,
            }
          : { x: 0, y: 0, width: 0, height: 0 };
      matches.push({
        id: `kw-${page}-${startIdx}`,
        page,
        rect,
        matchedText,
        redacted: false,
      });
    }
  }

  yield* put(KEYWORD_MATCHES_READY({ phrase: normalizedPhrase, matches }));
}

export function* keywordSearchSaga() {
  // takeLatest debounces both flows: a new KEYWORD_SEARCH cancels any in-
  // flight iteration, and toggling matchCase / wholeWord while typing
  // doesn't pile up parallel searches either. The debounce only gates
  // the typing path; option toggles re-run immediately.
  yield* takeLatest(KEYWORD_SEARCH, function* ({ payload: { phrase } }) {
    yield* delay(KEYWORD_SEARCH_DEBOUNCE_MS);
    yield* runKeywordSearch(phrase);
  });
  yield* takeLatest(SET_KEYWORD_SEARCH_OPTIONS, function* () {
    const session = yield* select(selectSession);
    if (!session) {
      return;
    }
    yield* runKeywordSearch(session.keywordSearch.phrase);
  });
  // Re-run the active search whenever a new page's text becomes
  // available. react-pdf lazy-renders pages so on first open only the
  // top of the document has its text in `pageTexts`; the saga's loop
  // would silently skip the rest and the user would see an under-count
  // (typically "matches on the first 3 pages only"). takeLatest +
  // delay coalesces the burst of SET_PAGE_TEXT events while pages
  // populate — one re-run after the burst settles, no per-page thrash.
  yield* takeLatest(SET_PAGE_TEXT, function* () {
    yield* delay(150);
    const session = yield* select(selectSession);
    if (!session || !session.keywordSearch.phrase.trim()) {
      return;
    }
    yield* runKeywordSearch(session.keywordSearch.phrase);
  });
  // The pre-pass (VE-25293) delivers every page's text in one
  // SET_PAGE_TEXT_BATCH instead of a burst of SET_PAGE_TEXT, so re-run the
  // active search once it lands — otherwise a search typed before the pre-pass
  // completes would keep its partial (render-path-only) result.
  yield* takeLatest(SET_PAGE_TEXT_BATCH, function* () {
    yield* delay(150);
    const session = yield* select(selectSession);
    if (!session || !session.keywordSearch.phrase.trim()) {
      return;
    }
    yield* runKeywordSearch(session.keywordSearch.phrase);
  });
}

// NOTE: keyword redactions (single "Redact" + "Redact all") are created by the
// DOM-driven walker `redactPhraseInDom` (wired in DocumentEditor), NOT by this
// saga — that walker produces glyph-tight geometry, whereas the saga's
// item-space `match.rect` is only accurate enough for the highlight overlay.
// The former KEYWORD_REDACT_ONE saga (and its item-space buildKeywordRedaction)
// was dead — its action was never dispatched — and has been removed.
